import { createStore, createHook, createActionsHook } from 'react-sweet-state';
import React, { createContext, useRef, useState } from 'react';

import { PermissionOptions } from './PermissionOptions.ts';
import { useBeforeUnload } from './useBeforeUnload.ts';
import { ContentType } from './ContentType.ts';

export type ExistingSpace = {
	spaceId: string;
	spaceKey: string;
	spaceName: string;
};

export const Store = createStore({
	initialState: {
		showImportContentDrawer: false,
		shouldRenderDrawer: false,
		showImportContentModal: false,
		shouldRenderModal: false,
		shouldRenderMiroTokenModal: false,
		contentType: ContentType.NOTION,
	},
	actions: {
		setShowImportContentDrawer:
			(value: boolean) =>
			({ setState }) => {
				setState({
					showImportContentDrawer: value,
				});
			},
		setShouldRenderDrawer:
			(value: boolean) =>
			({ setState }) => {
				setState({
					shouldRenderDrawer: value,
				});
			},
		setShowImportContentModal:
			(value: boolean) =>
			({ setState }) => {
				setState({
					showImportContentModal: value,
				});
			},
		setShouldRenderModal:
			(value: boolean) =>
			({ setState }) => {
				setState({
					shouldRenderModal: value,
				});
			},
		setShouldRenderMiroTokenModal:
			(value: boolean) =>
			({ setState }) => {
				setState({ shouldRenderMiroTokenModal: value });
			},
		setContentType:
			(value: ContentType) =>
			({ setState }) => {
				setState({
					contentType: value,
				});
			},
	},
	name: 'importContentStore',
});

export interface ImportContentWorkflowContextData {
	authProviderError: any;
	setAuthProviderError: (value: any) => void;
	hasSubpages: boolean;
	setHasSubpages: (value: boolean) => void;
	dropzoneCancelFnRef: React.MutableRefObject<Function | null>;
	browserCancelFnRef: React.MutableRefObject<Function | null>;
	isUploadingFile: boolean;
	setIsUploadingFile: (value: boolean) => void;
	isStartingImport: boolean;
	setIsStartingImport: (value: boolean) => void;
	fileNameDisplay: string;
	setFileNameDisplay: (value: string) => void;
	fileSizeBytes: number;
	setFileSizeBytes: (value: number) => void;
	filestoreId: string;
	setFilestoreId: (value: string) => void;
	migrationScopeId: string;
	setMigrationScopeId: (value: string) => void;
	migrationId: string;
	setMigrationId: (value: string) => void;
	collectionId: string;
	setCollectionId: (value: string) => void;
	shouldMapNotionMembers: boolean;
	setShouldMapNotionMembers: (value: boolean) => void;
	shouldRenderIntegrationTokenModal: boolean;
	setShouldRenderIntegrationTokenModal: (value: boolean) => void;
	shouldRenderCloseDrawerModal: boolean;
	setShouldRenderCloseDrawerModal: (value: boolean) => void;
	integrationToken: string;
	setIntegrationToken: (value: string) => void;
	shouldAddRemainingMembers: boolean;
	setShouldAddRemainingMembers: (value: boolean) => void;
	shouldCopyPagePermissions: boolean;
	setShouldCopyPagePermissions: (value: boolean) => void;
	shouldRenderAccessTokenModal: boolean;
	setShouldRenderAccessTokenModal: (value: boolean) => void;
	shouldExportEntities: boolean;
	setShouldExportEntities: (value: boolean) => void;
	accessToken: string;
	setAccessToken: (value: string) => void;
	refreshToken: string;
	setRefreshToken: (value: string) => void;
	tokenExpireAt: string;
	setTokenExpireAt: (value: string) => void;
	newSpaceName: string;
	setNewSpaceName: (value: string) => void;
	existingSpace?: ExistingSpace;
	setExistingSpace: (value?: ExistingSpace) => void;
	parentPageId?: string;
	setParentPageId: (value: string) => void;
	selectedPermissions: PermissionOptions;
	setSelectedPermissions: (value: PermissionOptions) => void;
	miroAuthToken: string;
	setMiroAuthToken: (value: string) => void;
	miroTeamId: string;
	setMiroTeamId: (value: string) => void;
	miroProjectId: string | null;
	setMiroProjectId: (value: string | null) => void;
	clearAllMiroBulkImportTokens: () => void;
	didReceiveMiroBulkApiTokens: boolean;
	setDidReceiveMiroBulkApiTokens: (value: boolean) => void;
	isNotionWorkspaceImportDisabled?: boolean;
	setIsNotionWorkspaceImportDisabled: (value: boolean) => void;
	isValidatingZip?: boolean;
	setIsValidatingZip: (value: boolean) => void;
}

export const ImportContentWorkflowContext = createContext<ImportContentWorkflowContextData>({
	authProviderError: '',
	setAuthProviderError: () => {},
	hasSubpages: false,
	setHasSubpages: () => {},
	dropzoneCancelFnRef: { current: null },
	browserCancelFnRef: { current: null },
	isUploadingFile: false,
	setIsUploadingFile: () => {},
	isStartingImport: false,
	setIsStartingImport: () => {},
	fileNameDisplay: '',
	setFileNameDisplay: () => {},
	fileSizeBytes: 0,
	setFileSizeBytes: () => {},
	filestoreId: '',
	setFilestoreId: () => {},
	migrationScopeId: '',
	setMigrationScopeId: () => {},
	migrationId: '',
	setMigrationId: () => {},
	collectionId: '',
	setCollectionId: () => {},
	shouldMapNotionMembers: true,
	setShouldMapNotionMembers: () => {},
	shouldRenderCloseDrawerModal: false,
	setShouldRenderCloseDrawerModal: () => {},
	shouldRenderIntegrationTokenModal: false,
	setShouldRenderIntegrationTokenModal: () => {},
	integrationToken: '',
	setIntegrationToken: () => {},
	shouldAddRemainingMembers: false,
	setShouldAddRemainingMembers: () => {},
	shouldCopyPagePermissions: false,
	setShouldCopyPagePermissions: () => {},
	shouldRenderAccessTokenModal: false,
	setShouldRenderAccessTokenModal: () => {},
	shouldExportEntities: true,
	setShouldExportEntities: () => {},
	accessToken: '',
	setAccessToken: () => {},
	refreshToken: '',
	setRefreshToken: () => {},
	tokenExpireAt: '',
	setTokenExpireAt: () => {},
	newSpaceName: '',
	setNewSpaceName: () => {},
	setExistingSpace: () => {},
	setParentPageId: () => {},
	selectedPermissions: PermissionOptions.DEFAULT,
	setSelectedPermissions: () => {},
	miroAuthToken: '',
	setMiroAuthToken: () => {},
	miroTeamId: '',
	setMiroTeamId: () => {},
	miroProjectId: null,
	setMiroProjectId: () => {},
	clearAllMiroBulkImportTokens: () => {},
	didReceiveMiroBulkApiTokens: false,
	setDidReceiveMiroBulkApiTokens: () => {},
	isNotionWorkspaceImportDisabled: false,
	setIsNotionWorkspaceImportDisabled: () => {},
	isValidatingZip: false,
	setIsValidatingZip: () => {},
});

export const ImportContentWorkflowContextProvider = (props: any) => {
	const [authProviderError, setAuthProviderError] = useState('');
	const [hasSubpages, setHasSubpages] = useState(false);
	const dropzoneCancelFnRef = useRef<Function | null>(null);
	const browserCancelFnRef = useRef<Function | null>(null);
	const [isUploadingFile, setIsUploadingFile] = useState(false);
	const [isStartingImport, setIsStartingImport] = useState(false);
	const [fileNameDisplay, setFileNameDisplay] = useState('');
	const [fileSizeBytes, setFileSizeBytes] = useState(0);
	const [filestoreId, setFilestoreId] = useState('');
	const [migrationScopeId, setMigrationScopeId] = useState('');
	const [migrationId, setMigrationId] = useState('');
	const [collectionId, setCollectionId] = useState('');
	const [shouldMapNotionMembers, setShouldMapNotionMembers] = useState(true);
	const [shouldRenderIntegrationTokenModal, setShouldRenderIntegrationTokenModal] = useState(false);
	const [shouldRenderCloseDrawerModal, setShouldRenderCloseDrawerModal] = useState(false);
	const [integrationToken, setIntegrationToken] = useState('');
	const [shouldAddRemainingMembers, setShouldAddRemainingMembers] = useState(true);
	const [shouldCopyPagePermissions, setShouldCopyPagePermissions] = useState(false);
	const [shouldRenderAccessTokenModal, setShouldRenderAccessTokenModal] = useState(false);
	const [shouldExportEntities, setShouldExportEntities] = useState(false);
	const [accessToken, setAccessToken] = useState('');
	const [refreshToken, setRefreshToken] = useState('');
	const [tokenExpireAt, setTokenExpireAt] = useState('');
	const [newSpaceName, setNewSpaceName] = useState('');
	const [existingSpace, setExistingSpace] = useState<ExistingSpace | undefined>();
	const [parentPageId, setParentPageId] = useState<string | undefined>();
	const [miroAuthToken, setMiroAuthToken] = useState('');
	const [miroTeamId, setMiroTeamId] = useState('');
	const [miroProjectId, setMiroProjectId] = useState<string | null>(null);
	const [didReceiveMiroBulkApiTokens, setDidReceiveMiroBulkApiTokens] = useState(false);
	const [selectedPermissions, setSelectedPermissions] = useState(PermissionOptions.DEFAULT);
	const [isNotionWorkspaceImportDisabled, setIsNotionWorkspaceImportDisabled] = useState(false);
	const [isValidatingZip, setIsValidatingZip] = useState(false);

	const clearAllMiroBulkImportTokens = () => {
		setMiroAuthToken('');
		setMiroTeamId('');
		setMiroProjectId(null);
	};

	const setParentPageIdWithValidation = (rawParentPageId: string | undefined) => {
		if (rawParentPageId === 'no-page-selection') {
			setParentPageId(undefined);
		} else {
			setParentPageId(rawParentPageId);
		}
	};

	const setMiroProjectIdWithValidation = (rawMiroProjectId: string | null) => {
		if (rawMiroProjectId === '' || rawMiroProjectId === null) {
			setMiroProjectId(null);
		} else {
			setMiroProjectId(rawMiroProjectId);
		}
	};

	useBeforeUnload('Changes may not be saved', isUploadingFile || isStartingImport);

	return (
		<ImportContentWorkflowContext.Provider
			// eslint-disable-next-line react/jsx-no-constructed-context-values
			value={{
				authProviderError,
				setAuthProviderError,
				hasSubpages,
				setHasSubpages,
				dropzoneCancelFnRef,
				browserCancelFnRef,
				isUploadingFile,
				setIsUploadingFile,
				isStartingImport,
				setIsStartingImport,
				fileNameDisplay,
				setFileNameDisplay,
				fileSizeBytes,
				setFileSizeBytes,
				filestoreId,
				setFilestoreId,
				migrationScopeId,
				setMigrationScopeId,
				migrationId,
				setMigrationId,
				collectionId,
				setCollectionId,
				shouldMapNotionMembers,
				setShouldMapNotionMembers,
				shouldRenderCloseDrawerModal,
				setShouldRenderCloseDrawerModal,
				shouldRenderIntegrationTokenModal,
				setShouldRenderIntegrationTokenModal,
				shouldExportEntities,
				setShouldExportEntities,
				integrationToken,
				setIntegrationToken,
				shouldAddRemainingMembers,
				setShouldAddRemainingMembers,
				shouldCopyPagePermissions,
				setShouldCopyPagePermissions,
				shouldRenderAccessTokenModal,
				setShouldRenderAccessTokenModal,
				accessToken,
				setAccessToken,
				refreshToken,
				setRefreshToken,
				tokenExpireAt,
				setTokenExpireAt,
				newSpaceName,
				setNewSpaceName,
				existingSpace,
				parentPageId,
				setParentPageId: setParentPageIdWithValidation,
				setExistingSpace,
				selectedPermissions,
				setSelectedPermissions,
				miroAuthToken,
				setMiroAuthToken,
				miroTeamId,
				setMiroTeamId,
				miroProjectId,
				setMiroProjectId: setMiroProjectIdWithValidation,
				clearAllMiroBulkImportTokens,
				didReceiveMiroBulkApiTokens,
				setDidReceiveMiroBulkApiTokens,
				isNotionWorkspaceImportDisabled,
				setIsNotionWorkspaceImportDisabled,
				isValidatingZip,
				setIsValidatingZip,
			}}
		>
			{props.children}
		</ImportContentWorkflowContext.Provider>
	);
};

export const useImportContentStore = createHook(Store);
export const useImportContentStoreActions = createActionsHook(Store);
